/**
 * Standalone API error utility functions.
 *
 * Extracted from the legacy src/api/templateVersionApi.ts during the
 * migration to api-generated. These are general-purpose error helpers
 * that work with the fetch-based API client's error responses.
 */

/**
 * Type guard utility for detecting 409 Conflict errors.
 * Works with fetch Response errors from the api-generated client.
 */
export function isConflictError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    // Handle ResponseError from api-generated runtime
    if ('response' in error) {
      const resp = (error as any).response;
      if (resp && typeof resp === 'object' && 'status' in resp) {
        return resp.status === 409;
      }
    }
    // Handle plain objects with status
    if ('status' in error) {
      return (error as any).status === 409;
    }
  }
  return false;
}

/**
 * Extracts a user-friendly error message from various error types.
 * Handles fetch Response errors from api-generated and generic Error instances.
 */
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    // Handle ResponseError from api-generated runtime
    if ('response' in error) {
      const resp = (error as any).response;
      if (resp && typeof resp === 'object' && 'status' in resp) {
        const status = resp.status;
        if (status === 409) {
          return 'Conflict: Another user has modified this draft. Please reload.';
        }
        if (status === 404) {
          return 'Resource not found.';
        }
        if (status === 400) {
          return 'Validation error. Please check your input.';
        }
        return `Server error (${status})`;
      }
    }
    // Handle plain objects with status
    if ('status' in error) {
      const status = (error as any).status;
      if (status === 409) {
        return 'Conflict: Another user has modified this draft. Please reload.';
      }
      if (status === 404) {
        return 'Resource not found.';
      }
      if (status === 400) {
        return 'Validation error. Please check your input.';
      }
      return `Server error (${status})`;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred.';
}
